/**
 * Strict numeric parsing for CSV/API boundaries (same contract as server/utils/csvNumber.js).
 * Rejects NaN, Infinity, and non-finite values; optional integer enforcement and ranges.
 */

function trimStr(v) {
  if (v == null) return "";
  return String(v).trim();
}

/** Spreadsheet / export placeholders treated as empty (N/A, —, etc.). */
export function isCsvPlaceholderValue(val) {
  const s = trimStr(val).toLowerCase();
  if (!s) return true;
  return ["n/a", "na", "#n/a", "-", "--", "—", "none", "null"].includes(s);
}

/**
 * Normalizes common spreadsheet / bank CSV number formats before {@link Number}.
 * Supports US-style thousands and decimal (1,234.56), optional $/€/£, and European-style
 * thousands/decimals (1.234,56). Plain values (-25, 625) pass through unchanged.
 * @param {unknown} raw
 * @returns {string}
 */
export function normalizeFormattedNumberString(raw) {
  let s = trimStr(raw);
  if (isCsvPlaceholderValue(s)) return "";
  s = s
    .replace(/[\s\u00A0]/g, "")
    .replace(/[$€£]/g, "");
  if (s.endsWith("%")) s = s.slice(0, -1);
  if (s === "") return "";
  const neg = s.startsWith("-");
  const pos = s.startsWith("+");
  if (neg || pos) s = s.slice(1);
  if (s === "") return neg ? "-" : pos ? "+" : "";

  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  if (lastDot > lastComma) {
    s = s.replace(/,/g, "");
  } else if (lastComma > lastDot) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (lastComma >= 0 && lastDot === -1) {
    if (/^\d{1,3}(,\d{3})+$/.test(s)) {
      s = s.replace(/,/g, "");
    } else {
      s = s.replace(",", ".");
    }
  } else if (lastDot >= 0 && lastComma === -1) {
    const dotCount = (s.match(/\./g) || []).length;
    if (dotCount > 1) {
      s = s.replace(/\./g, "");
    }
  }

  if (neg) s = "-" + s;
  else if (pos) s = "+" + s;
  return s;
}

/**
 * @param {unknown} value
 * @param {{ min?: number, max?: number, fieldName?: string }} [options]
 * @returns {{ ok: true, value: number } | { ok: false, error: string }}
 */
export function parseRequiredInt(value, options = {}) {
  const { min = -Infinity, max = Infinity, fieldName = "Value" } = options;
  let s = trimStr(value);
  if (s === "") return { ok: false, error: `${fieldName} is required` };
  s = normalizeFormattedNumberString(s);
  if (s === "" || s === "+" || s === "-") return { ok: false, error: `${fieldName} must be a whole number` };
  const n = Number(s);
  if (!Number.isFinite(n)) return { ok: false, error: `${fieldName} must be a finite number` };
  if (!Number.isInteger(n)) return { ok: false, error: `${fieldName} must be a whole number` };
  if (n < min || n > max) return { ok: false, error: `${fieldName} must be between ${min} and ${max}` };
  return { ok: true, value: n };
}

/**
 * Empty / whitespace → ok with `value: defaultIfEmpty` when provided, else `undefined`.
 * @param {{ min?: number, max?: number, fieldName?: string, defaultIfEmpty?: number }} [options]
 */
export function parseOptionalInt(value, options = {}) {
  const { min = -Infinity, max = Infinity, fieldName = "Value", defaultIfEmpty } = options;
  const s = trimStr(value);
  if (s === "") {
    if (defaultIfEmpty !== undefined) return { ok: true, value: defaultIfEmpty };
    return { ok: true, value: undefined };
  }
  return parseRequiredInt(s, { min, max, fieldName });
}

/**
 * @param {{ min?: number, max?: number, integerOnly?: boolean, fieldName?: string }} [options]
 */
export function parseRequiredFiniteNumber(value, options = {}) {
  const { min = -Infinity, max = Infinity, integerOnly = false, fieldName = "Value" } = options;
  let s = trimStr(value);
  if (s === "") return { ok: false, error: `${fieldName} is required` };
  s = normalizeFormattedNumberString(s);
  if (s === "" || s === "+" || s === "-") return { ok: false, error: `${fieldName} must be a finite number` };
  const n = Number(s);
  if (!Number.isFinite(n)) return { ok: false, error: `${fieldName} must be a finite number` };
  if (integerOnly && !Number.isInteger(n)) return { ok: false, error: `${fieldName} must be a whole number` };
  if (n < min || n > max) return { ok: false, error: `${fieldName} must be between ${min} and ${max}` };
  return { ok: true, value: n };
}

/**
 * @param {{ min?: number, max?: number, integerOnly?: boolean, fieldName?: string, defaultIfEmpty?: number }} [options]
 */
export function parseOptionalFiniteNumber(value, options = {}) {
  const { min = -Infinity, max = Infinity, integerOnly = false, fieldName = "Value", defaultIfEmpty } = options;
  const s = trimStr(value);
  if (s === "" || isCsvPlaceholderValue(s)) {
    if (defaultIfEmpty !== undefined) return { ok: true, value: defaultIfEmpty };
    return { ok: true, value: undefined };
  }
  return parseRequiredFiniteNumber(s, { min, max, integerOnly, fieldName });
}

/** When present, must be finite and >= 0 (optional higher `min` via options). */
export function parseOptionalNonNegativeFiniteNumber(value, options = {}) {
  const min = options.min != null ? Math.max(0, options.min) : 0;
  return parseOptionalFiniteNumber(value, { ...options, min });
}

/** When present, must be finite and > 0. */
export function parseOptionalPositiveFiniteNumber(value, options = {}) {
  const { fieldName = "Value" } = options;
  const s = trimStr(value);
  if (s === "") {
    if (options.defaultIfEmpty !== undefined) return { ok: true, value: options.defaultIfEmpty };
    return { ok: true, value: undefined };
  }
  const normalized = normalizeFormattedNumberString(s);
  if (normalized === "" || normalized === "+" || normalized === "-") {
    return { ok: false, error: `${fieldName} must be a finite number` };
  }
  const n = Number(normalized);
  if (!Number.isFinite(n)) return { ok: false, error: `${fieldName} must be a finite number` };
  if (n <= 0) return { ok: false, error: `${fieldName} must be greater than 0` };
  return { ok: true, value: n };
}
